import React, { useState } from 'react';
import '../AccountTypes/AccountTypeForm.css';
import '../../../shared/StyledInput.css';
import RequiredMark from '../../../shared/RequiredMark';
import CancelButton from '../../../shared/CancelButton';
import SaveButton from '../../../shared/SaveButton';
import { formatBsb } from '../../../shared/paymentOptions';

// Add/Edit an Account (issue #341). An Account under a bank Account Type
// also holds bank details: account name, BSB and account number.
function AccountForm({ initial, accountTypes, onSave, onCancel, loading }) {
  const [form, setForm] = useState({
    account_type_id: initial?.account_type_id || '',
    account_number: initial?.account_number ?? '',
    account_name: initial?.account_name || '',
    bank_account_name: initial?.bank_account_name || '',
    bank_bsb: initial?.bank_bsb ? formatBsb(initial.bank_bsb) : '',
    bank_account_number: initial?.bank_account_number || '',
  });
  const [error, setError] = useState('');
  const set = (key) => (e) => setForm((prev) => ({ ...prev, [key]: e.target.value }));

  // An inactive Type still shows while it's the one this Account is under.
  const typeOptions = accountTypes.filter((t) => t.is_active || String(t.id) === String(initial?.account_type_id));
  const selectedType = typeOptions.find((t) => String(t.id) === String(form.account_type_id));
  const isBank = !!selectedType?.is_bank;

  const validate = () => {
    if (!form.account_type_id) return 'Account Type is required.';
    if (!String(form.account_number).match(/^\d+$/) || Number(form.account_number) < 1) return 'Account number must be a positive whole number.';
    if (!form.account_name.trim()) return 'Account name is required.';
    if (isBank && form.bank_bsb.trim() && form.bank_bsb.replace(/[\s-]/g, '').length !== 6) return 'BSB must be 6 digits.';
    return '';
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const err = validate();
    if (err) {
      setError(err);
      return;
    }
    setError('');
    onSave({
      account_type_id: Number(form.account_type_id),
      account_number: Number(form.account_number),
      account_name: form.account_name.trim(),
      ...(isBank ? {
        bank_account_name: form.bank_account_name.trim(),
        bank_bsb: form.bank_bsb,
        bank_account_number: form.bank_account_number,
      } : {}),
    });
  };

  return (
    <form className="account-type-form" onSubmit={handleSubmit} style={{ minWidth: 360 }}>
      <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap' }}>
        <div style={{ minWidth: 260 }}>
          <label>Account Type<RequiredMark /></label><br />
          <select className="styled-input" value={form.account_type_id} onChange={set('account_type_id')}>
            <option value="">Select Account Type</option>
            {typeOptions.map((t) => (
              <option key={t.id} value={t.id}>
                {t.account_number} {t.type_name} ({t.account_class?.account_category?.category_name} › {t.account_class?.class_name})
              </option>
            ))}
          </select>
        </div>
        <div>
          <label>Account Number<RequiredMark /></label><br />
          <input
            className="styled-input"
            type="number"
            min="1"
            value={form.account_number}
            onChange={set('account_number')}
            style={{ width: 100 }}
            placeholder="e.g., 1110"
          />
        </div>
        <div style={{ minWidth: 260 }}>
          <label>Account Name<RequiredMark /></label><br />
          <input
            className="styled-input"
            value={form.account_name}
            onChange={set('account_name')}
            maxLength={100}
            placeholder="e.g., Business Cheque Account"
          />
        </div>
      </div>

      {isBank && (
        <div style={{ display: 'flex', gap: 14, marginTop: 10, flexWrap: 'wrap' }}>
          <div style={{ minWidth: 260 }}>
            <label>Bank Account Name</label><br />
            <input className="styled-input" value={form.bank_account_name} onChange={set('bank_account_name')} maxLength={100} placeholder="e.g., Acme Pty Ltd" />
          </div>
          <div>
            <label>BSB</label><br />
            <input className="styled-input" value={form.bank_bsb} onChange={set('bank_bsb')} maxLength={7} placeholder="062-000" style={{ width: 110 }} />
          </div>
          <div>
            <label>Bank Account Number</label><br />
            <input className="styled-input" value={form.bank_account_number} onChange={set('bank_account_number')} maxLength={20} style={{ width: 180 }} />
          </div>
        </div>
      )}

      {error && <div style={{ color: 'red', marginTop: 8 }}>{error}</div>}

      <div style={{ marginTop: 12 }}>
        <SaveButton loading={loading} />
        <CancelButton onClick={onCancel} disabled={loading} style={{ marginLeft: 8 }} />
      </div>
    </form>
  );
}

export default AccountForm;
